import React, {
  createRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import DatePicker from 'react-datepicker';

import 'react-datepicker/dist/react-datepicker.css';
import './CreateListingComponent.scss';
import {
  ErrorMessage,
  Field,
  FieldArray,
  Form,
  Formik,
  useFormikContext,
} from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import DropZone from '../../CommonComponents/DropZone/DropZone';
import { usePermissions } from '../../../utils/Permission/Permission';
import FormikFormInput from '../../../utils/FormikFormInput';
import FormikFormSelectField from '../../../utils/FormikFormSelect';
import axiosCall from '../../../utils/axiosCall';
import { ToastType, showToastMessage } from '../../../utils/ToastOptionCommon';
import DisableSection from '../../../utils/DisableSection/DisableSection';
import { ListingSection } from '../../../constants/ListingSection';
import { BsPencilFill } from 'react-icons/bs';
import { IoCloseCircleOutline, IoWarningOutline } from 'react-icons/io5';
import { ListingDetailSchema } from '../../../utils/yupSchemas/CreateListingSchema';
import Loader from '../../CommonComponents/Loader/Loader';
import { createMultipartFormData } from '../../../utils/createMultipartFormdata';
import {
  propertyById,
  resetpropertyState,
} from '../../../redux/slices/PropertySlice';
import { AppDispatch } from '../../../redux/store';
import { setFormDirty } from '../../../redux/slices/LocationSlice';
import {
  dealPropertyType,
  dealType,
  dealTypeEnum,
  propertyType,
} from '../../../constants/Property';
import FormikFormRadioButton from '../../../utils/FormikFormRadioButton';
import { useSubscription } from '../../../utils/CustomHooks/Subscription';
import { loggedInUserState } from '../../../redux/slices/LoginSlice';
import { setCreateListing } from '../../../redux/slices/CreateLisingSlice';

const sellRequiredFields = [
  {
    id: 'superBuiltUpArea',
    label: 'Super Built-up Area (sq ft)',
    type: 'number',
  },
  { id: 'carpetArea', label: 'Carpet Area (sq ft)', type: 'number' },
  { id: 'constructionYear', label: 'Construction Year', type: 'number' },
  {
    id: 'furnishingStatus',
    label: 'Furnishing Status',
    type: 'select',
    options: [
      { value: 'unfurnished', label: 'Unfurnished' },
      { value: 'semifurnished', label: 'Semi-Furnished' },
      { value: 'furnished', label: 'Fully Furnished' },
    ],
  },
  {
    id: 'facing',
    label: 'Property Facing',
    type: 'select',
    options: [
      { value: 'north', label: 'North' },
      { value: 'south', label: 'South' },
      { value: 'east', label: 'East' },
      { value: 'west', label: 'West' },
      { value: 'northeast', label: 'North East' },
      { value: 'northwest', label: 'North West' },
      { value: 'southeast', label: 'South East' },
      { value: 'southwest', label: 'South West' },
    ],
  },
];

function CreateListingComponent({
  isEdit,
  handleSubmit,
  currentSection,
  setCurrentSection,
  initialValues,
  savedSection,
}: {
  isEdit?: boolean;
  handleSubmit: (formData: FormData) => void;
  currentSection: ListingSection;
  setCurrentSection: any;
  initialValues: any;
  savedSection?: any;
}) {
  // const showBedroomLabelFor = ['house', 'apartment', 'condos'];
  const defaultInitialValues = {
    ...initialValues,
    details: {
      ...initialValues.details,
      superBuiltUpArea: '',
      superBuiltUpAreaUnit: 'sqft',
      carpetArea: '',
      carpetAreaUnit: 'sqft',
      constructionYear: '',
      furnishingStatus: '',
      facing: '',
      propertyAge: 0,
      possessionStatus: '',
      totalFloors: 0,
      floorNumber: 0,
      buildingDetails: {
        ...initialValues.details?.buildingDetails,
        yearBuilt: initialValues.details?.buildingDetails?.yearBuilt || '',
        lastRenovatedYear:
          initialValues.details?.buildingDetails?.lastRenovatedYear || '',
        renovationDetails:
          initialValues.details?.buildingDetails?.renovationDetails || '',
        condition: initialValues.details?.buildingDetails?.condition || '',
      },
    },
  };
  const [initialFormValue, setInitialFormValue] =
    useState(defaultInitialValues);
  const showBedroomLabelFor = ['house', 'flat', 'villa'];
  const { hasPermission } = usePermissions();
  const [isAdmin, setIsAdmin] = useState(false);
  const userData = useSelector(loggedInUserState);
  const [loading, setLoading] = useState(false);
  const [owner, setOwner] = useState([]);
  const [files1, setFiles1] = useState<any>([]);
  const [files2, setFiles2] = useState<any>([]);
  const [files3, setFiles3] = useState<any>([]);
  const [files4, setFiles4] = useState<any>([]);
  const [imgLoading, setImgLoading] = useState(false);
  const propertyState = useSelector((state: any) => state.property);
  const property = useSelector(propertyById);
  const formObjectRef = createRef<any>();
  const dispatch = useDispatch<AppDispatch>();
  const foodOption = [
    { label: 'Yes', value: 'yes' },
    { label: 'No', value: 'no' },
  ];
  const { photosCount, freeTrialDays, isActive, featureProperty } =
    useSubscription();

  useEffect(() => {
    setInitialFormValue(initialValues);
  }, [initialValues]);

  useEffect(() => {
    if (
      propertyState.success &&
      (property?.section === ListingSection.Details || property?.section === -1)
    ) {
      getRawImages();
      dispatch(resetpropertyState());
    }
  }, [propertyState.success, property?.section]);

  useEffect(() => {
    dispatch(resetpropertyState());
  }, [property?.details?.propertyType]);

  const getRawImages = async () => {
    const room1ImagesUrl = property?.rooms?.[0]?.imagesLink || [];
    const room2ImagesUrl = property?.rooms?.[1]?.imagesLink || [];
    const room3ImagesUrl = property?.rooms?.[2]?.imagesLink || [];
    const room4ImagesUrl = property?.rooms?.[3]?.imagesLink || [];

    setImgLoading(true);
    try {
      const loadImages = async (images: string[]) =>
        Promise.all(
          images.map(async (url) => {
            const filename = extractFilenameFromUrl(url);
            const fileImg = await fetchAndConvertImage(url, filename);
            return { url, name: filename, fileImg };
          })
        );

      if (property.dealType === dealTypeEnum.Sell) {
        setFiles1(await loadImages(property?.imagesLink || []));
      } else {
        const [room1Images, room2Images, room3Images, room4Images] =
          await Promise.all([
            loadImages(room1ImagesUrl),
            loadImages(room2ImagesUrl),
            loadImages(room3ImagesUrl),
            loadImages(room4ImagesUrl),
          ]);
        setFiles1(room1Images);
        setFiles2(room2Images);
        setFiles3(room3Images);
        setFiles4(room4Images);
      }
    } catch (error) {
      console.error('Error loading existing listing images:', error);
    } finally {
      setImgLoading(false);
    }
  };

  function extractFilenameFromUrl(url: string): string {
    // Split the URL by '/'
    const parts = url?.split('/');
    // Get the last part of the path which should contain the filename
    const filenameWithParams = parts[parts?.length - 1];
    // Extract the filename by removing any query parameters
    const filename = filenameWithParams?.split('?')[0];
    return filename;
  }
  async function fetchAndConvertToBlob(url: string): Promise<any> {
    try {
      const response = await fetch(url);
      // console.log({response});
      const blob = await response.blob();
      return blob;
    } catch (error) {
      console.log(error);
    }
    return;
  }

  function blobToFile(blob: Blob, fileName: string): File {
    // Create a new File object from the Blob
    return new File([blob], fileName, { type: blob?.type });
  }

  async function fetchAndConvertImage(img: string, fileName: string) {
    try {
      const blob = await fetchAndConvertToBlob(img);
      const file = blobToFile(blob, fileName);
      return file;
    } catch (error) {
      console.error('Error fetching or converting image:', error);
      throw error;
    }
  }

  useEffect(() => {
    let role = userData?.currentRole;
    const storedUser = localStorage.getItem('user');
    if (!role && storedUser !== null) {
      const user = JSON.parse(storedUser);
      role = user?.currentRole;
    }
    if (role === 'Admin') {
      setIsAdmin(true);
      getAllOwner();
    }
  }, []);

  const getAllOwner = async () => {
    try {
      setLoading(true);
      const url = `/user/all-owners`;
      const result = await axiosCall.get(url);
      if (result && result.data?.owners) {
        const d = result.data?.owners?.map((e: any) => ({
          value: `${e.firstName} ${e.lastName}`,
          label: `${e.firstName} ${e.lastName}`,
          id: e._id,
        }));
        setOwner(d);
      }
      // setOccupations([{ name: 'Select Occupation', id: 0 }, ...result.data]);
    } catch (error) {
      showToastMessage('Failed to get owners', ToastType.Error);
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const emptyRoomDetails = {
    name: '',
    bathroomType: 'shared',
    price: '',
    depositMoney: '',
    cleaningCharges: '',
    petFees: '',
    availableFrom: new Date()?.toISOString(),
    isFeaturedProperty: false,
  };

  const submitHandler = (values: any) => {
    let newProperty: any = {
      section: values.section,
    };
    let isError = false;
    const formData = new FormData();
    const data = { ...values.details };
    let owner_s: any = undefined;
    let userId = isEdit ? property?.user_id : userData?._id;
    if (userData.currentRole === 'Admin') {
      owner_s = owner.find((e: any) => e.value === data.owner);
      if (owner_s) {
        userId = owner_s?.id;
      }
    }

    // Base property data
    newProperty = {
      ...newProperty,
      title: data.propertyTitle,
      description: data.propertyDescription,
      p_type: data.propertyType,
      status: data.propertyStatus,
      numberOfRooms: data.propertyRooms,
      user_id: userId,
      dealType: data.dealType,
      isEntireUnit: data.isEntireUnit || false,
      is_active: false,
      isFeaturedProperty: data.isFeaturedProperty || false,
      buildingDetails: {
        yearBuilt: data.buildingDetails?.yearBuilt || undefined,
        lastRenovatedYear:
          data.buildingDetails?.lastRenovatedYear || undefined,
        renovationDetails: data.buildingDetails?.renovationDetails || '',
        condition: data.buildingDetails?.condition || undefined,
      },
      views: 0,
      totalFavorites: 0,
    };

    // Add sell-specific fields if deal type is sell
    if (data.dealType === 'sell') {
      newProperty = {
        ...newProperty,
        superBuiltUpArea: data.superBuiltUpArea,
        superBuiltUpAreaUnit: data.superBuiltUpAreaUnit,
        carpetArea: data.carpetArea,
        carpetAreaUnit: data.carpetAreaUnit,
        constructionYear: data.constructionYear,
        furnishingStatus: data.furnishingStatus,
        facing: data.facing,
        propertyAge: data.propertyAge,
        possessionStatus: data.possessionStatus,
        totalFloors: data.totalFloors || 0,
        floorNumber: data.floorNumber || 0,
        totalBathroom: data.totalBathroom || 0,
        price: data.propertyRoomType[0].price,
        available_date: data.propertyRoomType[0].availableFrom,
      };
    }

    // Add rental-specific fields if deal type is rent
    if (data.dealType === dealTypeEnum.Rent) {
      newProperty = {
        ...newProperty,
        totalBathroom: data.totalBathroom,
        totalBed: data.totalBed,
        withFood: data.withFood,
        noOfGarages: Number(data.noOfGarages || 0),
      };
    }

    // Add rooms data based on deal type
    newProperty.rooms = data.propertyRoomType?.map((room: any) => {
      const roomData: any = {
        name: String(room.name),
        price: Number(room.price) || 0,
        availableFrom: room.availableFrom,
        isFeaturedProperty: room?.isFeaturedProperty,
      };

      // Add rental-specific room fields only for rent
      if (data.dealType === dealTypeEnum.Rent) {
        roomData.bathroomType = room.bathroomType;
        roomData.depositMoney = Number(room.depositMoney) || 0;
        roomData.cleaningCharges = Number(room.cleaningCharges) || 0;
        roomData.petFees = Number(room.petFees) || 0;
        roomData.totalPerson = room?.totalPerson ?? 0;
      }

      return roomData;
    });
    for (let i = 0; i < newProperty?.rooms?.length; i++) {
      if (i === 0 && files1?.length === 0) {
        isError = true;
        break;
      } else if (i === 1 && files2?.length === 0) {
        isError = true;
        break;
      } else if (i === 2 && files3?.length === 0) {
        isError = true;
        break;
      } else if (i === 3 && files4?.length === 0) {
        isError = true;
        break;
      }
    }
    if (isError) {
      return;
    }
    // Create Form
    createMultipartFormData(newProperty, formData);
    for (let i = 0; i < files1?.length; i++) {
      formData.append(`room1/images`, files1?.[i].fileImg);
    }
    for (let i = 0; i < files2?.length; i++) {
      formData.append(`room2/images`, files2?.[i].fileImg);
    }
    for (let i = 0; i < files3?.length; i++) {
      formData.append(`room3/images`, files3?.[i].fileImg);
    }
    for (let i = 0; i < files4?.length; i++) {
      formData.append(`room4/images`, files4?.[i].fileImg);
    }

    handleSubmit(formData);

    dispatch(setCreateListing({ ...newProperty }));
  };

  const populateArray = (value: any) => {
    formObjectRef.current.populateArray(value);
  };

  const FormObject = React.forwardRef((props, ref) => {
    const { setFieldValue, values, dirty, isSubmitting } =
      useFormikContext<any>();
    const propertyTypeRef = useRef(values.details.propertyType);

    useEffect(() => {
      if (dirty && !isSubmitting) {
        dispatch(setFormDirty(dirty));
      }
    }, [dirty, isSubmitting]);

    const populateArray = (value: number) => {
      if (values.details.isEntireUnit || values.details.dealType === dealTypeEnum.Sell) {
        setFieldValue('details.propertyRooms', value);
      } else if (value < values.details.propertyRoomType.length) {
        let x = [...values.details.propertyRoomType];
        x.length = value;
        setFieldValue('details.propertyRooms', value);
        setFieldValue('details.propertyRoomType', x);
      } else if (value > values.details.propertyRoomType.length) {
        const d = value - values.details.propertyRoomType.length;
        const arr = new Array(d).fill(emptyRoomDetails);
        let x = [...values.details.propertyRoomType, ...arr];
        setFieldValue('details.propertyRooms', value);
        setFieldValue('details.propertyRoomType', x);
      }
    };

    useImperativeHandle(ref, () => ({
      populateArray,
    }));

    useEffect(() => {
      if (
        propertyTypeRef.current &&
        values.details.propertyType !== propertyTypeRef.current
      ) {
        propertyTypeRef.current = values.details.propertyType;
        setFieldValue('details.isEntireUnit', false);
      } else if (!propertyTypeRef.current) {
        propertyTypeRef.current = values.details.propertyType;
      }
    }, [values.details.propertyType]);
    return null;
  });

  return (
    <Formik
      initialValues={{
        ...initialFormValue,
        isAdmin: isAdmin,
        section: ListingSection.Details,
      }}
      validationSchema={ListingDetailSchema}
      enableReinitialize={true}
      onSubmit={submitHandler}
    >
      {({
        values,
        setFieldValue,
        touched,
        errors,
        isSubmitting,
        resetForm,
      }: any) => (
        <Form>
          {imgLoading && <Loader />}
          <FormObject ref={formObjectRef} />
          <div id="create-listing">
            {currentSection !== ListingSection.Details && <DisableSection />}
            <div className="box-view">
              <h3 className="box-title">Create Listing</h3>
              {(isEdit || savedSection[ListingSection.Details]) && (
                <div title="Edit" className="edit-property">
                  {currentSection !== ListingSection.Details && (
                    <button
                      type="button"
                      className="edit-btn-listing"
                      onClick={() => {
                        setCurrentSection(ListingSection.Details);
                      }}
                    >
                      <BsPencilFill className="pencil_icon" />
                    </button>
                  )}
                  {currentSection === ListingSection.Details && (
                    <button
                      type="button"
                      className="edit-btn-listing"
                      onClick={() => {
                        setCurrentSection(-1);
                        resetForm();
                      }}
                    >
                      <IoCloseCircleOutline className="cancel_icon" />
                    </button>
                  )}
                </div>
              )}
              {hasPermission('admin') ? (
                <div className="row">
                  <div className="col-12 mb-3">
                    <label className="form-label">Owner</label>
                    <Field
                      as={FormikFormSelectField}
                      isLabelValueSame={true}
                      id="owner"
                      name="details.owner"
                      className="form-select"
                      options={owner}
                      loading={loading}
                    />
                  </div>
                </div>
              ) : (
                <></>
              )}

              <div className="row">
                <div className="col-md-4 mb-3">
                  <label className="form-label" htmlFor="dealType">
                    Deal Type
                  </label>
                  <Field
                    as="select"
                    id="dealType"
                    name="details.dealType"
                    className="form-select"
                    onChange={(e: any) => {
                      const newType = e.target.value;

                      if (newType === dealTypeEnum.Sell) {
                        showToastMessage(
                          'We will launch this facility soon.',
                          ToastType.Info
                        );
                        setFieldValue('details.dealType', dealTypeEnum.Rent);
                        return;
                      }

                      setFieldValue('details.dealType', newType);

                      // Set default property type based on deal type
                      if (newType === 'sell') {
                        setFieldValue('details.propertyType', 'flat');
                      } else if (newType === dealTypeEnum.Rent) {
                        setFieldValue('details.propertyType', 'room');
                        setFieldValue('details.isEntireUnit', false);
                      }

                      // Reset all form values while keeping the new deal type and property type
                      const values = {
                        ...initialValues,
                        details: {
                          ...initialValues.details,
                          dealType: newType,
                          propertyType: newType === 'sell' ? 'flat' : 'room',
                          isEntireUnit: false,
                          propertyRooms: 1,
                          carpetAreaUnit: 'sqft',
                          superBuiltUpAreaUnit: 'sqft',
                          propertyRoomType: [
                            {
                              name: '',
                              bathroomType: 'shared',
                              price: '',
                              depositMoney: '',
                              cleaningCharges: '',
                              petFees: '',
                              availableFrom: new Date()?.toISOString(),
                              isFeaturedProperty: false,
                            },
                          ],
                          buildingDetails: {
                            yearBuilt: '',
                            lastRenovatedYear: '',
                            renovationDetails: '',
                            condition: '',
                          },
                        },
                        isAdmin,
                        section: ListingSection.Details,
                      };

                      // Reset files
                      setFiles1([]);
                      setFiles2([]);
                      setFiles3([]);
                      setFiles4([]);

                      // Reset form with new values
                      setInitialFormValue(values);
                      resetForm({
                        values: values,
                      });
                    }}
                  >
                    {dealType.map((e) => (
                      <option key={e.id} value={e.value}>
                        {e.label}
                      </option>
                    ))}
                  </Field>
                  <ErrorMessage
                    component="div"
                    name="details.dealType"
                    className="invalid-feedback"
                  />
                </div>
                {/* <div className="col-md-4 mb-3">
                  <label className="form-label" htmlFor="propertyType">
                    Property Type
                  </label>
                  <Field
                    as="select"
                    id="propertyType"
                    name="details.propertyType"
                  />
                </div> */}

                <div className="col-md-4 mb-3">
                  <label className="form-label" htmlFor="propertyType">
                    Property Type
                  </label>
                  <Field
                    as="select"
                    id="propertyType"
                    name="details.propertyType"
                    className="form-select"
                    onChange={(e: any) => {
                      const newType = e.target.value;
                      setFieldValue('details.propertyType', newType);
                      // Reset files when property type changes
                      setFiles1([]);
                      setFiles2([]);
                      setFiles3([]);
                      setFiles4([]);
                    }}
                  >
                    {values.details.dealType === dealTypeEnum.Rent
                      ? propertyType.map((e) => {
                          return (
                            <option key={e.id} value={e.value}>
                              {e.label}
                            </option>
                          );
                        })
                      : dealPropertyType.map((e) => {
                          return (
                            <option key={e.id} value={e.value}>
                              {e.label}
                            </option>
                          );
                        })}
                  </Field>
                  <ErrorMessage
                    component="div"
                    name="details.propertyType"
                    className="invalid-feedback"
                  />
                </div>
                <div className="col-md-4 mb-3">
                  <label className="form-label" htmlFor="propertyTitle">
                    Property Title
                  </label>
                  <Field
                    type="text"
                    name="details.propertyTitle"
                    placeholder=""
                    autoComplete="off"
                    className={`form-control`}
                    as={FormikFormInput}
                  />
                </div>
                <div className="col-12 mb-3">
                  <label className="form-label" htmlFor="propertyDescription">
                    Description
                  </label>
                  <Field
                    component="textarea"
                    name="details.propertyDescription"
                    placeholder=""
                    autoComplete="off"
                    className={`form-control form-textarea ${
                      touched.propertyDescription && errors.propertyDescription
                        ? 'is-invalid'
                        : ''
                    }`}
                  />
                  <ErrorMessage
                    component="div"
                    name="details.propertyDescription"
                    className="invalid-feedback"
                  />
                </div>
              </div>

              <div className="row">
                {/* <div className="col-md-4 mb-3">
                  <label className="form-label" htmlFor="propertyStatus">
                    Status
                  </label>
                  <Field
                    as="select"
                    name="details.propertyStatus"
                    className="form-select"
                    disabled
                  >
                    <option value="InActive">In Active</option>
                    <option value="Active">Active</option>
                  </Field>
                  <ErrorMessage
                    component="div"
                    name="details.propertyStatus"
                    className="invalid-feedback"
                  />
                </div> */}

                <div className="col-md-4 col-sm-6 mt-3">
                  <div className="entire-unit-checkbox">
                    <Field
                      type="checkbox"
                      id="feature-property-checkbox"
                      name="details.isFeaturedProperty"
                      className="form-check-input me-2"
                      checked={values.details.isFeaturedProperty}
                    />
                    <label htmlFor="feature-property-checkbox" className="cp">
                      Mark as featured property
                    </label>
                  </div>
                </div>
                
                {(values.details.propertyType === 'boys_pg' ||
                  values.details.propertyType === 'girls_pg') && (
                  <>
                    <div className="col-md-4 mb-3">
                      <label className="form-label">Number of Bed</label>
                      <Field
                        type="number"
                        name="details.totalBed"
                        className="form-control"
                        as={FormikFormInput}
                      />
                    </div>

                    <div className="col-md-4 mb-3">
                      <label className="form-label">Food Available</label>
                      <div className="food-radio-group">
                        {foodOption.map((option) => (
                          <label
                            key={option.value}
                            className={`food-radio-option${
                              values.details.withFood === option.value
                                ? ' selected'
                                : ''
                            }`}
                          >
                            <Field
                              type="radio"
                              name="details.withFood"
                              value={option.value}
                            />
                            {option.label}
                          </label>
                        ))}
                        <ErrorMessage
                          component="div"
                          name="details.withFood"
                          className="invalid-feedback"
                        />
                      </div>
                    </div>
                  </>
                )}

                {values.details.propertyType !== 'boys_pg' &&
                  values.details.propertyType !== 'girls_pg' && (
                    <div className="col-md-4 mb-3">
                      <div>
                        <label className="form-label" htmlFor="propertyRooms">
                          {showBedroomLabelFor.includes(
                            values.details.propertyType
                          ) || values.details.dealType === dealTypeEnum.Sell
                            ? 'Bedrooms'
                            : 'Rooms'}
                        </label>
                        <Field
                          as="select"
                          name="details.propertyRooms"
                          onChange={(e: any) => {
                            populateArray(parseInt(e.target.value));
                          }}
                          className="form-select"
                        >
                          {[1, 2, 3, 4].map((val) => (
                            <option key={val} value={val}>
                              {val}
                            </option>
                          ))}
                        </Field>
                        <ErrorMessage
                          component="div"
                          name="details.propertyRooms"
                          className="invalid-feedback"
                        />
                      </div>
                    </div>
                  )}
                {showBedroomLabelFor.includes(values.details.propertyType) &&
                  values.details.dealType !== 'sell' && (
                    <div className="col-md-4 col-sm-6 mt-3">
                      <div className="entire-unit-checkbox">
                        <Field
                          type="checkbox"
                          id="entire-unit-checkbox"
                          name="details.isEntireUnit"
                          className="form-check-input me-2"
                          checked={values.details.isEntireUnit}
                          onChange={() => {
                            setFieldValue('details.propertyRooms', 1);
                            setFieldValue('details.propertyRoomType', [
                              emptyRoomDetails,
                            ]);
                            setFieldValue(
                              'details.isEntireUnit',
                              !values.details.isEntireUnit
                            );
                          }}
                        />
                        <label htmlFor="entire-unit-checkbox" className="cp">
                          Entire Unit
                        </label>
                      </div>
                    </div>
                  )}
                {(values.details.dealType === dealTypeEnum.Sell ||
                  values.details.isEntireUnit) && (
                  <div className="col-md-4 mb-3">
                    <label className="form-label">Bathroom(s)</label>
                    <Field
                      type="number"
                      name={`details.totalBathroom`}
                      className={`form-control`}
                      as={FormikFormInput}
                    />
                  </div>
                )}
              </div>

              <FieldArray
                name="propertyRoomType"
                render={() => (
                  <>
                    {values.details.propertyRoomType?.map(
                      (_: any, index: number) => {
                        const ctx = `details.propertyRoomType[${index}]`;
                        return (
                          <div
                            key={index}
                            className="room-section row align-items-center border-bottom mb-3 bg-light shadow-sm pt-3 mt-2"
                          >
                            {values.details.dealType !== dealTypeEnum.Sell &&
                              !values.details.isEntireUnit && (
                                <>
                                  <div className="col-md-4 mb-3">
                                    <label className="form-label">
                                      {/* {showBedroomLabelFor.includes(
                                      values.details.propertyType
                                    )
                                      ? 'Bedrooms'
                                      : 'Rooms'} */}
                                      Rooms{' '}
                                      <span className="hint-text">
                                        (Single/Double Bed Room, Master Room,
                                        etc.)
                                      </span>
                                    </label>
                                    <Field
                                      type="text"
                                      name={`${ctx}.name`}
                                      className="form-control"
                                      as={FormikFormInput}
                                    />
                                  </div>
                                  <div className="col-md-4 mb-3">
                                    <label className="form-label">
                                      Bathroom type
                                    </label>
                                    <Field
                                      as="select"
                                      name={`${ctx}.bathroomType`}
                                      className="form-select"
                                    >
                                      {['shared', 'private'].map((val) => (
                                        <option key={val} value={val}>
                                          {val
                                            .charAt(0)
                                            .toUpperCase()
                                            .concat(val.slice(1))}
                                        </option>
                                      ))}
                                    </Field>
                                    <ErrorMessage
                                      component="div"
                                      name={`${ctx}.bathroomType`}
                                      className="invalid-feedback"
                                    />
                                  </div>
                                  {values.details.propertyType === 'room' && (
                                    <div className="col-md-4 mb-3">
                                      <label className="form-label">
                                        Number of Person
                                      </label>
                                      <Field
                                        type="number"
                                        name={`${ctx}.totalPerson`}
                                        className={`form-control`}
                                        as={FormikFormInput}
                                      />
                                    </div>
                                  )}
                                </>
                              )}
                            {/* Available From DatePicker */}
                            <div className="col-md-4 mb-3">
                              <label className="form-label">
                                Available From
                              </label>
                              <DatePicker
                                selected={
                                  values.details.propertyRoomType[index]
                                    ?.availableFrom
                                }
                                className="form-control"
                                name={`${ctx}.availableFrom`}
                                onChange={(date: any) =>
                                  setFieldValue(
                                    `${ctx}.availableFrom`,
                                    date?.toISOString()
                                  )
                                }
                                dateFormat="MM/dd/yyyy"
                                minDate={new Date()}
                              />
                              <ErrorMessage
                                component="div"
                                name={`${ctx}.availableFrom`}
                                className="invalid-feedback"
                              />
                            </div>
                            {/* Second row */}
                            {/* Image Field */}
                            {/* <div className="col-md-4 mb-3">
                        <label className="form-label">Image</label>
                        <div className="input-group">
                          <input type="file" className="form-control" id="Image" />
                          <label className="input-group-text" htmlFor="Image">
                            Upload
                          </label>
                        </div>                 
                      </div> */}
                            {/* Price Fields */}
                            <div className="col-md-8 col-lg-4 mb-md-3">
                              <div className="row">
                                <div
                                  className={`${
                                    values.details.dealType === 'sell'
                                      ? 'col-12'
                                      : 'col-md-6'
                                  } mb-3 mb-md-0`}
                                >
                                  <label className="form-label">
                                    {values.details.dealType === 'sell'
                                      ? 'Asking Price'
                                      : 'Price per month'}
                                  </label>

                                  <Field
                                    type="number"
                                    name={`${ctx}.price`}
                                    className={`form-control`}
                                    as={FormikFormInput}
                                    min={1}
                                  />
                                </div>
                                {values.details.dealType === dealTypeEnum.Rent && (
                                  <div className="col-md-6 mb-3 mb-md-0">
                                    <label className="form-label">
                                      Deposit
                                    </label>
                                    <Field
                                      type="number"
                                      name={`${ctx}.depositMoney`}
                                      className="form-control"
                                      min={0}
                                    ></Field>
                                  </div>
                                )}
                              </div>
                            </div>

                            {values.details.dealType === dealTypeEnum.Rent && (
                              <div className="col-md-8 col-lg-4 mb-md-3">
                                <div className="row">
                                  <div className="col-md-6 mb-3 mb-md-0">
                                    <label className="form-label">
                                      Cleaning Fee{' '}
                                      <span className="hint-text">
                                        (If any){' '}
                                      </span>
                                    </label>
                                    <Field
                                      type="number"
                                      name={`${ctx}.cleaningCharges`}
                                      className="form-control"
                                      min={0}
                                    ></Field>
                                  </div>
                                  <div className="col-md-6 mb-3 mb-md-0">
                                    <label className="form-label">
                                      Pet Fee{' '}
                                      <span className="hint-text">
                                        (If any){' '}
                                      </span>
                                    </label>
                                    <Field
                                      type="number"
                                      name={`${ctx}.petFees`}
                                      className="form-control"
                                      min={0}
                                    ></Field>
                                  </div>
                                </div>
                              </div>
                            )}

                            {values.details.dealType === 'sell' && (
                              <div className="row sell-specific-fields">
                                <div className="col-md-6 mb-3">
                                  <label className="form-label">
                                    Super Built-up Area
                                  </label>
                                  <div className="d-flex gap-2">
                                    <div className="flex-grow-1">
                                      <Field
                                        type="number"
                                        name="details.superBuiltUpArea"
                                        className="form-control"
                                        placeholder="Enter super built-up area"
                                      />
                                      <ErrorMessage
                                        name="details.superBuiltUpArea"
                                        component="div"
                                        className="invalid-feedback"
                                      />
                                    </div>
                                    <div style={{ width: '120px' }}>
                                      <Field
                                        as="select"
                                        name="details.superBuiltUpAreaUnit"
                                        className="form-select"
                                      >
                                        <option value="sqft">Sq Ft</option>
                                        <option value="sqmt">Sq Mt</option>
                                        <option value="sqyd">Sq Yd</option>
                                        <option value="biswa">Biswa</option>
                                        <option value="acres">Acres</option>
                                        <option value="hectares">
                                          Hectares
                                        </option>
                                      </Field>
                                    </div>
                                  </div>
                                </div>

                                <div className="col-md-6 mb-3">
                                  <label className="form-label">
                                    Carpet Area
                                  </label>
                                  <div className="d-flex gap-2">
                                    <div className="flex-grow-1">
                                      <Field
                                        type="number"
                                        name="details.carpetArea"
                                        className="form-control"
                                        placeholder="Enter carpet area"
                                      />
                                      <ErrorMessage
                                        name="details.carpetArea"
                                        component="div"
                                        className="invalid-feedback"
                                      />
                                    </div>
                                    <div style={{ width: '120px' }}>
                                      <Field
                                        as="select"
                                        name="details.carpetAreaUnit"
                                        className="form-select"
                                      >
                                        <option value="sqft">Sq Ft</option>
                                        <option value="sqmt">Sq Mt</option>
                                        <option value="sqyd">Sq Yd</option>
                                        <option value="biswa">Biswa</option>
                                        <option value="acres">Acres</option>
                                        <option value="hectares">
                                          Hectares
                                        </option>
                                      </Field>
                                    </div>
                                  </div>
                                </div>

                                <div className="col-md-4 mb-3">
                                  <label className="form-label">
                                    Construction Year
                                  </label>
                                  <Field
                                    type="number"
                                    name="details.constructionYear"
                                    className="form-control"
                                    placeholder="Enter construction year"
                                  />
                                  <ErrorMessage
                                    name="details.constructionYear"
                                    component="div"
                                    className="invalid-feedback"
                                  />
                                </div>

                                <div className="col-md-4 mb-3">
                                  <label className="form-label">
                                    Furnishing Status
                                  </label>
                                  <Field
                                    as="select"
                                    name="details.furnishingStatus"
                                    className="form-select"
                                  >
                                    <option value="">
                                      Select Furnishing Status
                                    </option>
                                    <option value="unfurnished">
                                      Unfurnished
                                    </option>
                                    <option value="semifurnished">
                                      Semi-Furnished
                                    </option>
                                    <option value="furnished">
                                      Fully Furnished
                                    </option>
                                  </Field>
                                  <ErrorMessage
                                    name="details.furnishingStatus"
                                    component="div"
                                    className="invalid-feedback"
                                  />
                                </div>

                                <div className="col-md-4 mb-3">
                                  <label className="form-label">
                                    Property Facing
                                  </label>
                                  <Field
                                    as="select"
                                    name="details.facing"
                                    className="form-select"
                                  >
                                    <option value="">
                                      Select Property Facing
                                    </option>
                                    <option value="north">North</option>
                                    <option value="south">South</option>
                                    <option value="east">East</option>
                                    <option value="west">West</option>
                                    <option value="northeast">
                                      North East
                                    </option>
                                    <option value="northwest">
                                      North West
                                    </option>
                                    <option value="southeast">
                                      South East
                                    </option>
                                    <option value="southwest">
                                      South West
                                    </option>
                                  </Field>
                                  <ErrorMessage
                                    name="details.facing"
                                    component="div"
                                    className="invalid-feedback"
                                  />
                                </div>

                                <div className="col-md-4 mb-3">
                                  <label className="form-label">
                                    Property Age
                                  </label>
                                  <Field
                                    as="select"
                                    name="details.propertyAge"
                                    className="form-select"
                                  >
                                    <option value="">
                                      Select Property Age
                                    </option>
                                    <option value="new">
                                      New Construction
                                    </option>
                                    <option value="<5">
                                      Less than 5 years
                                    </option>
                                    <option value="5-10">5-10 years</option>
                                    <option value="10+">
                                      More than 10 years
                                    </option>
                                  </Field>
                                  <ErrorMessage
                                    name="details.propertyAge"
                                    component="div"
                                    className="invalid-feedback"
                                  />
                                </div>

                                <div className="col-md-4 mb-3">
                                  <label className="form-label">
                                    Possession Status
                                  </label>
                                  <Field
                                    as="select"
                                    name="details.possessionStatus"
                                    className="form-select"
                                  >
                                    <option value="">
                                      Select Possession Status
                                    </option>
                                    <option value="ready">Ready to Move</option>
                                    <option value="underConstruction">
                                      Under Construction
                                    </option>
                                  </Field>
                                  <ErrorMessage
                                    name="details.possessionStatus"
                                    component="div"
                                    className="invalid-feedback"
                                  />
                                </div>

                                {values.details.propertyType === 'flat' && (
                                  <>
                                    <div className="col-md-4 mb-3">
                                      <label className="form-label">
                                        Total Floors in Building
                                      </label>
                                      <Field
                                        type="number"
                                        name="details.totalFloors"
                                        className="form-control"
                                        placeholder="Enter total number of floors"
                                        min={1}
                                      />
                                      <ErrorMessage
                                        name="details.totalFloors"
                                        component="div"
                                        className="invalid-feedback"
                                      />
                                    </div>

                                    <div className="col-md-4 mb-3">
                                      <label className="form-label">
                                        Floor Number
                                      </label>
                                      <Field
                                        type="number"
                                        name="details.floorNumber"
                                        className="form-control"
                                        placeholder="Enter floor number"
                                        min={0}
                                      />
                                      <ErrorMessage
                                        name="details.floorNumber"
                                        component="div"
                                        className="invalid-feedback"
                                      />
                                    </div>
                                  </>
                                )}

                                <div className="col-md-4 mb-3">
                                  <label className="form-label">Year Built</label>
                                  <Field
                                    type="number"
                                    name="details.buildingDetails.yearBuilt"
                                    className="form-control"
                                    placeholder="Enter year built"
                                  />
                                  <ErrorMessage
                                    name="details.buildingDetails.yearBuilt"
                                    component="div"
                                    className="invalid-feedback"
                                  />
                                </div>

                                <div className="col-md-4 mb-3">
                                  <label className="form-label">
                                    Last Renovated Year
                                  </label>
                                  <Field
                                    type="number"
                                    name="details.buildingDetails.lastRenovatedYear"
                                    className="form-control"
                                    placeholder="Enter renovation year"
                                  />
                                  <ErrorMessage
                                    name="details.buildingDetails.lastRenovatedYear"
                                    component="div"
                                    className="invalid-feedback"
                                  />
                                </div>

                                <div className="col-md-4 mb-3">
                                  <label className="form-label">
                                    Building Condition
                                  </label>
                                  <Field
                                    as="select"
                                    name="details.buildingDetails.condition"
                                    className="form-select"
                                  >
                                    <option value="">Select Condition</option>
                                    <option value="NEW">New</option>
                                    <option value="EXCELLENT">Excellent</option>
                                    <option value="GOOD">Good</option>
                                    <option value="FAIR">Fair</option>
                                  </Field>
                                </div>

                                <div className="col-12 mb-3">
                                  <label className="form-label">
                                    Renovation Details
                                  </label>
                                  <Field
                                    as="textarea"
                                    name="details.buildingDetails.renovationDetails"
                                    className="form-control form-textarea"
                                    placeholder="Describe any renovations"
                                  />
                                </div>
                              </div>
                            )}

                            {/* <div className="col-md-4 col-sm-6 mt-md-2">
                              <label className="form-check-label">
                                <Field
                                  type="checkbox"
                                  name={`${ctx}.isFeaturedProperty`}
                                  className="form-check-input me-2"
                                />
                                Is this is a feature property?
                              </label>
                            </div> */}
                            {/* Third Row Image upload*/}
                            <div className="col-md-12 mt-3">
                              <DropZone
                                files={
                                  index === 0
                                    ? files1
                                    : index === 1
                                    ? files2
                                    : index === 2
                                    ? files3
                                    : files4
                                }
                                setFiles={
                                  index === 0
                                    ? setFiles1
                                    : index === 1
                                    ? setFiles2
                                    : index === 2
                                    ? setFiles3
                                    : setFiles4
                                }
                                className="bg-light border text-center p-4 rounded-3"
                                page="addListing"
                                numberOfFiles={isActive ? photosCount : 0}
                                maxFileSizeInBytes={5242880} // 5mb
                                isSubmit={isSubmitting}
                                section={currentSection}
                              />
                            </div>
                          </div>
                        );
                      }
                    )}
                  </>
                )}
              />

              {values.details.dealType === dealTypeEnum.Rent && (
                <div className="row building-details-fields">
                  <div className="col-md-4 mb-3">
                    <label className="form-label">Year Built</label>
                    <Field
                      type="number"
                      name="details.buildingDetails.yearBuilt"
                      className="form-control"
                      placeholder="Enter year built"
                    />
                    <ErrorMessage
                      name="details.buildingDetails.yearBuilt"
                      component="div"
                      className="invalid-feedback"
                    />
                  </div>

                  <div className="col-md-4 mb-3">
                    <label className="form-label">Last Renovated Year</label>
                    <Field
                      type="number"
                      name="details.buildingDetails.lastRenovatedYear"
                      className="form-control"
                      placeholder="Enter renovation year"
                    />
                    <ErrorMessage
                      name="details.buildingDetails.lastRenovatedYear"
                      component="div"
                      className="invalid-feedback"
                    />
                  </div>

                  <div className="col-md-4 mb-3">
                    <label className="form-label">Building Condition</label>
                    <Field
                      as="select"
                      name="details.buildingDetails.condition"
                      className="form-select"
                    >
                      <option value="">Select Condition</option>
                      <option value="NEW">New</option>
                      <option value="EXCELLENT">Excellent</option>
                      <option value="GOOD">Good</option>
                      <option value="FAIR">Fair</option>
                    </Field>
                  </div>

                  <div className="col-12 mb-3">
                    <label className="form-label">Renovation Details</label>
                    <Field
                      as="textarea"
                      name="details.buildingDetails.renovationDetails"
                      className="form-control form-textarea"
                      placeholder="Describe any renovations"
                    />
                  </div>
                </div>
              )}

              <div id="locationTab" className="row my-3">
                {isEdit && currentSection === ListingSection.Details && (
                  <div className="col-12 text-center">
                    <a href="#locationTab">
                      <button
                        type="submit"
                        className="btn btn-primary"
                        // onClick={() => submit?.click()}
                      >
                        Update
                      </button>
                    </a>
                  </div>
                )}
                {!isEdit && currentSection === ListingSection.Details && (
                  <div className="col-12 text-center">
                    <a href="#locationTab">
                      <button
                        type="submit"
                        className="btn btn-primary"
                        // onClick={() => submit?.click()}
                      >
                        Save Draft
                      </button>
                    </a>
                  </div>
                )}
              </div>
            </div>
          </div>
        </Form>
      )}
    </Formik>
  );
}

export default CreateListingComponent;
